import React, { useState, useEffect, useRef } from 'react';
import {
  ChevronsUpDown,
  Check,
  Search,
  Loader2,
  X,
  Building2,
  Plus
} from 'lucide-react';
import OrganisationService from '../../../services/OrganisationService/OrganisationService';
import Organisation from '../../../types/Organisation';
import Button from '../../Button/Button';
import CustomTooltip from '../../CustomTooltip/CustomTooltip';

interface OrganisationSelectorProps {
  selectedOrganisation: Organisation | null;
  onSelect: (organisation: Organisation) => void;
  onCreate?: () => void;
  collapsed?: boolean;
}

const RESULTS_PER_PAGE = 50;
const MAX_VISIBLE_CHARS = 22;
const SEARCH_DEBOUNCE_MS = 150;
const ERROR_DISPLAY_MS = 5000;

interface FetchOrganisationsSetters {
  setOptions: React.Dispatch<React.SetStateAction<Organisation[]>>;
  setHasMore: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<React.SetStateAction<string>>;
  setShowError: React.Dispatch<React.SetStateAction<boolean>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

async function fetchAndApplyOrganisations(
  search: string,
  page: number,
  setters: FetchOrganisationsSetters
): Promise<void> {
  const { setOptions, setHasMore, setError, setShowError, setLoading } =
    setters;
  try {
    const res = await OrganisationService.searchOrganisations(
      search,
      page,
      RESULTS_PER_PAGE
    );
    setOptions((prev) =>
      page === 0 ? res.results : [...prev, ...res.results]
    );
    setHasMore(res.results.length === RESULTS_PER_PAGE);
  } catch {
    setError('Error fetching organisations');
    setShowError(true);
    setTimeout(() => setShowError(false), ERROR_DISPLAY_MS);
  } finally {
    setLoading(false);
  }
}

function OrganisationSelector({
  selectedOrganisation,
  onSelect,
  onCreate,
  collapsed = false
}: Readonly<OrganisationSelectorProps>): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);
  const [options, setOptions] = useState<Organisation[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const prevSearchRef = useRef(search);

  const organisationName =
    selectedOrganisation?.name ?? 'Select an organisation';
  const shouldShowTooltip = organisationName.length > MAX_VISIBLE_CHARS;

  const handleTogglePopover = (): void => {
    if (!isOpen) {
      setOptions([]);
      setPage(0);
      setHasMore(true);
      setSearch('');
      prevSearchRef.current = '';
      setLoading(true);
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const isSearchChange = prevSearchRef.current !== search;
    prevSearchRef.current = search;
    const delay = isSearchChange ? SEARCH_DEBOUNCE_MS : 0;

    setLoading(true);
    const handle = setTimeout(() => {
      fetchAndApplyOrganisations(search, page, {
        setOptions,
        setHasMore,
        setError,
        setShowError,
        setLoading
      });
    }, delay);

    return (): void => clearTimeout(handle);
  }, [search, isOpen, page]);

  const handleListScroll = (e: React.UIEvent<HTMLDivElement>): void => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (!loading && hasMore && scrollTop + clientHeight >= scrollHeight - 10) {
      setPage((p) => p + 1);
    }
  };

  const handleSelect = (organisation: Organisation): void => {
    onSelect(organisation);
    setIsOpen(false);
  };

  useEffect(() => {
    setPage(0);
    setHasMore(true);
    setOptions([]);
    if (isOpen) {
      setLoading(true);
    }
  }, [search, isOpen]);

  useEffect(() => {
    if (isOpen) {
      searchInputRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        isOpen &&
        popoverRef.current &&
        triggerRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return (): void =>
      document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className={collapsed ? 'relative' : 'relative w-full px-0 py-0.5'}>
      <div ref={triggerRef}>
        {collapsed ? (
          <CustomTooltip content={organisationName} className='text-white'>
            <Button
              variant='outline'
              onClick={handleTogglePopover}
              aria-label='Switch organisation'
              className={`h-10 w-10 p-0 flex items-center justify-center bg-[#057D99]
                text-white border-white/30 hover:bg-[#012438] hover:text-white
                rounded-md transition-colors`}
            >
              <Building2 className='h-5 w-5' />
            </Button>
          </CustomTooltip>
        ) : (
          <Button
            variant='outline'
            onClick={handleTogglePopover}
            className={`w-full justify-between bg-[#057D99] text-white border-white/30 hover:bg-[#012438] hover:text-white
             h-10 pl-4 pr-2 py-2 rounded-md transition-colors text-base`}
          >
            <div className='flex items-center min-w-0 flex-1'>
              <Building2 className='mr-2 h-4 w-4 flex-shrink-0 opacity-80' />
              {shouldShowTooltip ? (
                <CustomTooltip
                  content={organisationName}
                  className='text-white'
                >
                  <span className='truncate'>{organisationName}</span>
                </CustomTooltip>
              ) : (
                <span className='truncate'>{organisationName}</span>
              )}
            </div>
            <ChevronsUpDown className='ml-4 h-4 w-4 shrink-0 opacity-50' />
          </Button>
        )}
      </div>

      {isOpen && (
        <div
          ref={popoverRef}
          className={`absolute ${collapsed ? 'left-full top-0 ml-2' : 'top-full left-0 mt-1'}
            bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden`}
          style={{ width: 'min(384px, calc(100vw - 6rem))' }}
        >
          <div className='flex items-center border-b px-4 py-3'>
            <Search size={16} className='text-gray-400 mr-2 flex-shrink-0' />
            <input
              ref={searchInputRef}
              type='text'
              placeholder='Search organisation...'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className='flex-1 text-base bg-transparent outline-none text-gray-900 placeholder:text-gray-400'
            />
            {search && (
              <button
                type='button'
                onClick={() => {
                  setSearch('');
                  searchInputRef.current?.focus();
                }}
                className='ml-2 text-gray-400 hover:text-gray-600 flex-shrink-0'
                aria-label='Clear search'
              >
                <X size={16} />
              </button>
            )}
          </div>

          {loading && (
            <div className='flex justify-center items-center py-4'>
              <Loader2 size={20} className='animate-spin text-gray-400' />
            </div>
          )}
          {!loading && options.length === 0 && (
            <div className='flex justify-center items-center py-4 text-gray-500 text-sm'>
              No organisations found
            </div>
          )}
          {!loading && options.length > 0 && (
            <div
              className='max-h-60 overflow-y-auto'
              onScroll={handleListScroll}
            >
              {options.map((organisation) => (
                <Button
                  key={organisation.id}
                  variant='ghost'
                  className='flex items-center justify-between px-3 py-2 hover:bg-gray-50 w-full text-left h-auto text-base font-normal'
                  onClick={() => handleSelect(organisation)}
                >
                  <div className='flex items-center flex-1 min-w-0'>
                    <div className='w-6 flex justify-center mr-2'>
                      {selectedOrganisation?.id === organisation.id && (
                        <Check size={16} className='text-green-600' />
                      )}
                    </div>
                    <div className='text-base text-gray-900 truncate flex-1 min-w-0'>
                      {organisation.name}
                    </div>
                  </div>
                </Button>
              ))}
            </div>
          )}

          {onCreate && (
            <button
              type='button'
              onClick={() => {
                setIsOpen(false);
                onCreate();
              }}
              className='flex items-center gap-2 w-full px-4 py-3 border-t text-base text-[#057D99] hover:bg-gray-50'
            >
              <Plus size={16} className='flex-shrink-0' />
              Create organisation
            </button>
          )}
        </div>
      )}

      {showError && (
        <div className='fixed bottom-4 left-4 bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg z-50'>
          <div className='flex items-center gap-2'>
            <span className='text-sm'>{error}</span>
            <button
              type='button'
              onClick={() => setShowError(false)}
              className='text-white/70 hover:text-white ml-2'
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrganisationSelector;
